import { useState, useCallback } from 'react';
import axiosInstance from '../axiosInstance';

const CMS_STATIC_PAGES = '/cms/static-pages';

/**
 * Fired on window after every successful save, with the saved { groups, pages }
 * as its detail.
 *
 * The sidebar lists every static page and is mounted once for the whole
 * session. Without this it kept the list it loaded when the dashboard opened:
 * a page added in the editor was live on the website at once, but missing from
 * the menu until the browser was reloaded.
 */
export const STATIC_PAGES_SAVED_EVENT = 'staticPagesSaved';

/**
 * The endpoint always answers with { success, data: { groups, pages } }.
 * Everything downstream expects those two arrays to exist, so normalise here.
 */
function toStaticPagesData(res) {
  return {
    groups: Array.isArray(res?.data?.groups) ? res.data.groups : [],
    pages: Array.isArray(res?.data?.pages) ? res.data.pages : [],
  };
}

/**
 * Read the static-page tree. Returns { execute, loading, error }.
 * execute({ all }) — pass all: true (admin) to include inactive groups/pages.
 */
export function useGetStaticPages() {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const execute = useCallback(async ({ all = false } = {}) => {
    setLoading(true);
    setError(null);
    try {
      const { data: res } = await axiosInstance.get(CMS_STATIC_PAGES, {
        params: all ? { all: true } : undefined,
      });
      return toStaticPagesData(res);
    } catch (err) {
      const msg = err.response?.data?.message || 'Failed to load static pages';
      setError(msg);
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  return { execute, loading, error };
}

/**
 * Replace the whole static-page tree. Returns { execute, loading, error }.
 * execute(payload) where payload = { groups: [Group], pages: [Page] }.
 */
export function useUpdateStaticPages() {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const execute = useCallback(async (payload) => {
    setLoading(true);
    setError(null);
    try {
      const { data: res } = await axiosInstance.put(CMS_STATIC_PAGES, payload);
      const saved = toStaticPagesData(res);
      window.dispatchEvent(new CustomEvent(STATIC_PAGES_SAVED_EVENT, { detail: saved }));
      return saved;
    } catch (err) {
      const msg = err.response?.data?.message || 'Failed to save static pages';
      setError(msg);
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  return { execute, loading, error };
}

/**
 * Attach a PDF to a section.
 *
 * Stores the file and resolves to { documentUrl, fileName, sizeBytes }. The
 * section is not changed here: the caller writes the URL into its own state and
 * the author still has to save the page, so an upload started and abandoned
 * leaves the page exactly as it was.
 */
export function useUploadStaticPageDocument() {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const uploadDocument = useCallback(async (file) => {
    setLoading(true);
    setError(null);
    try {
      const formData = new FormData();
      formData.append('document', file);
      const { data } = await axiosInstance.post(`${CMS_STATIC_PAGES}/document`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      return data?.data ?? null;
    } catch (err) {
      const msg = err.response?.data?.message || 'Failed to upload the document';
      setError(msg);
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  return { uploadDocument, loading, error };
}
